/* Single source of truth for the subtitle-language choices, shared by the
   upload-config card (SubtitleSettingsModal) and the editor's RegenerateModal
   so the two lists never drift apart. Values must match the backend enum in
   backend/src/models/Job.js. */
export const LANGUAGE_OPTIONS = [
  { value: 'hinglish', label: 'Hinglish (Romanised)', shortLabel: 'Hinglish' },
  { value: 'hindi', label: 'Hindi (Translate, Devanagari)', shortLabel: 'Hindi' },
  { value: 'marathi', label: 'Marathi (Romanised)', shortLabel: 'Marathi' },
  { value: 'marathi_devanagari', label: 'Marathi (Translate, Devanagari)', shortLabel: 'Marathi (Devanagari)' },
  { value: 'banglish', label: 'Banglish (Romanised Bengali)', shortLabel: 'Banglish' },
  { value: 'bengali', label: 'Bengali (Translate, Bangla script)', shortLabel: 'Bengali' },
  { value: 'tenglish', label: 'Tenglish (Romanised Telugu)', shortLabel: 'Tenglish' },
  { value: 'telugu', label: 'Telugu (Translate, Telugu script)', shortLabel: 'Telugu' },
  { value: 'tanglish', label: 'Tanglish (Romanised Tamil)', shortLabel: 'Tanglish' },
  { value: 'tamil', label: 'Tamil (Translate, Tamil script)', shortLabel: 'Tamil' },
  { value: 'gujlish', label: 'Gujlish (Romanised Gujarati)', shortLabel: 'Gujlish' },
  { value: 'gujarati', label: 'Gujarati (Translate, Gujarati script)', shortLabel: 'Gujarati' },
  { value: 'roman_urdu', label: 'Roman Urdu (Romanised Urdu)', shortLabel: 'Roman Urdu' },
  { value: 'urdu', label: 'Urdu (Translate, Urdu script)', shortLabel: 'Urdu' },
  { value: 'english', label: 'English (Translate)', shortLabel: 'English' },
];

/* The same choices grouped the way the picker shows them: one row per language,
   with its Roman-script and native-script modes as the two chips inside the row.
   `glyph` is the badge letter (a script letter for the Indic languages, "Aa" for
   English), `native` the language's own name for the native-script chip. */
export const LANGUAGE_GROUPS = [
  { id: 'hindi', name: 'Hindi', glyph: 'अ', modes: [{ value: 'hinglish', label: 'Hinglish', hint: 'Roman script' }, { value: 'hindi', label: 'हिंदी', hint: 'Devanagari' }] },
  { id: 'marathi', name: 'Marathi', glyph: 'म', modes: [{ value: 'marathi', label: 'Romanised', hint: 'Roman script' }, { value: 'marathi_devanagari', label: 'मराठी', hint: 'Devanagari' }] },
  { id: 'bengali', name: 'Bengali', glyph: 'ব', modes: [{ value: 'banglish', label: 'Banglish', hint: 'Roman script' }, { value: 'bengali', label: 'বাংলা', hint: 'Bangla script' }] },
  { id: 'telugu', name: 'Telugu', glyph: 'తె', modes: [{ value: 'tenglish', label: 'Tenglish', hint: 'Roman script' }, { value: 'telugu', label: 'తెలుగు', hint: 'Telugu script' }] },
  { id: 'tamil', name: 'Tamil', glyph: 'த', modes: [{ value: 'tanglish', label: 'Tanglish', hint: 'Roman script' }, { value: 'tamil', label: 'தமிழ்', hint: 'Tamil script' }] },
  { id: 'gujarati', name: 'Gujarati', glyph: 'ગ', modes: [{ value: 'gujlish', label: 'Gujlish', hint: 'Roman script' }, { value: 'gujarati', label: 'ગુજરાતી', hint: 'Gujarati script' }] },
  { id: 'urdu', name: 'Urdu', glyph: 'اُ', modes: [{ value: 'roman_urdu', label: 'Roman Urdu', hint: 'Roman script' }, { value: 'urdu', label: 'اردو', hint: 'Urdu script' }] },
  { id: 'english', name: 'English', glyph: 'Aa', modes: [{ value: 'english', label: 'English', hint: 'Translated from any language' }] },
];

/** The group and mode a subtitleLanguage value belongs to (Hinglish when unknown). */
export const findLanguageMode = (value) => {
  for (const group of LANGUAGE_GROUPS) {
    const mode = group.modes.find((m) => m.value === value);
    if (mode) return { group, mode };
  }
  return { group: LANGUAGE_GROUPS[0], mode: LANGUAGE_GROUPS[0].modes[0] };
};
